import { createPayment } from '../../../network/api/payment';
import {
  createAccount, getInfos, setInfos, getIsOwner,
} from '../../../network/api/project';
import { getInvoice } from '../../../network/api/invoicing';
import { addToast } from '../toasts/actions';
import type { AppDispatch, RootState } from 'services/store/types';
import type { Project, ProjectPayload } from 'types/Project';
import { logError } from 'utils/logs';

export function setUserInfos() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch({
        type: 'FETCH_USER_DATA',
        payload: {
          vatVerification: true,
        },
      });
      const { user } = getState();
      const value: ProjectPayload = {
        id_ayomi: user.projectId,
        name: user.projectName,
        entity: {
          society_name: user.projectName,
          legal_name: user.projectName,
          tva: user.vatNumber,
        },
        project_actors: {
          types: 'P2P',
          user: {
            email: user.email,
            first_name: user.firstName,
            last_name: user.lastName,
          },
        },
      };
      if (user.addressSociety.zip_code && user.addressSociety.city && user.addressSociety.country) {
        value.entity.address = user.addressSociety;
      } if (user.address.country && user.address.zip_code && user.address.city) {
        value.project_actors.user.address = user.address;
      }
      const { data } = await setInfos(value);
      dispatch({
        type: 'FETCH_USER_DATA',
        payload: {
          verifyVatNumber: data.entity.vat_rate === 0 ? 'invoicingForm.validVat' : 'invoicingForm.invalidVat',
          vatRate: data.entity.vat_rate,
          vatVerification: false,
        },
      });
    } catch (err) {
      logError(err as Error);
      console.log(err);
    }
  };
}

export function getPaymentRedirectionLink(paymentMethod: string) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch({ type: 'MAKE_PAYMENT_REQUEST' });
    try {
      await setUserInfos()(dispatch, getState);
      const { user, config } = getState();
      const value = {
        amount: config.amountRegistration,
        type: 'set_up',
        success_url: `${window.location.origin}/downloadInvoice?idAyomi=${user.projectId}`,
        cancel_url: `${window.location.origin}/invoicingForm?status=cancel&idAyomi=${user.projectId}`,
        id_project: user.projectId,
        payment_method: [paymentMethod],
        siren: user.siren,
        from_company: user.fromCompany,
        company_name: user.projectName,
        addressCompany: user.addressSociety,
        address: user.address,
      };
      const { data } = await createPayment(value);
      if (data.url) {
        window.location.assign(data.url);
      } else {
        dispatch({
          type: 'MAKE_PAYMENT_SUCCESS', payload: data,
        });
      }
    } catch (err) {
      logError(err as Error);
      dispatch({ type: 'MAKE_PAYMENT_FAILED', payload: err });
      dispatch(addToast({
        type: 'error',
        title: 'Le paiement est momentanément indisponible',
        description: `Une erreur technique est survenue lors de l'envoie de votre demande. ${err}`,
      }));
    }
  };
}

export function postCreateProject() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { eligible, user } = getState();
      if (user.isUploading) return;
      dispatch({ type: 'SEND_CREATE_PROJECT' });
      const value = {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
        fundingType: eligible.typeInvestment,
        turnover: eligible.amountCA,
        country: eligible.country,
        siren: eligible.siren,
        tva: user.vatNumber,
        amountSought: eligible.amountSought,
        results: eligible.amountRN,
        tokenManageo: '',
        lead: '',
        ticketMinimum: '',
      };
      const { data } = await createAccount(value);
      if (data?.id_ayomi) {
        dispatch({ type: 'CREATE_PROJECT_SUCCESS', payload: { projectId: data.id_ayomi, projectName: data.name } });
      } else if (data?.error === 'Account already used') dispatch({ type: 'CREATE_PROJECT_ALREADY_USED' });
      else dispatch({ type: 'CREATE_PROJECT_FAILED' });
    } catch (err) {
      logError(err as Error);
      dispatch({ type: 'CREATE_PROJECT_FAILED' });
    }
  };
}

async function isOwner(dispatch: AppDispatch, getState: () => RootState) {
  try {
    const urlidAyomi = new URLSearchParams(window.location.search).get('idAyomi');
    const savedData = localStorage.getItem('ayomi_project') || '{}';
    dispatch({ type: 'FETCH_LOCAL_DATA', payload: JSON.parse(savedData) });
    if (urlidAyomi) dispatch({ type: 'UPDATE_PROJECT_ID', payload: parseFloat(urlidAyomi) });
    const { data } = await getIsOwner(getState().user.projectId);
    dispatch({ type: 'UPDATE_IS_OWNER', payload: data?.status });
  } catch (err) {
    logError(err as Error);
    dispatch({ type: 'UPDATE_IS_OWNER', payload: false });
    console.error(err);
  }
}

export function getUserInfos() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      await isOwner(dispatch, getState);
      const user = getState().user;
      if (user) {
        const { data }: { data: Project } = await getInfos(user.projectId);
        const p2p = data.project_actors.filter((pa) => pa.types === 'P2P')[0];
        localStorage.setItem('ayomi_project', JSON.stringify({
          "projectId": data.id,
          "projectName": data.name,
          "amountSought": data?.fund_raised?.amount_sought || 0,
        }));
        dispatch({
          type: 'FETCH_USER_DATA',
          payload: {
            projectName: data.entity.society_name,
            amountSought: data.fund_raised.amount_sought,
            addressSociety: {
              address: data.entity.address?.address || user.address.address,
              zip_code: data.entity.address?.zip_code || user.address.zip_code,
              city: data.entity.address?.city || user.address.city,
              country: data.entity.address?.country || user.address.country,
            },
            firstName: p2p.user.first_name,
            lastName: p2p.user.last_name,
            address: {
              address: p2p.user.address?.address || user.address.address,
              zip_code: p2p.user.address?.zip_code || user.address.zip_code,
              city: p2p.user.address?.city || user.address.city,
              country: p2p.user.address?.country || user.address.country,
            },
            companyLegalName: data.entity.society_name,
            siren: data.entity.siren,
            vatNumber: data.entity.tva,
            verifyVatNumber: data.entity.vat_rate === 0 ? 'invoicingForm.validVat' : 'invoicingForm.invalidVat',
            vatRate: data.entity.vat_rate,
            invoiceAddressSociety: {
              address: data.entity.address?.address || user.address.address,
              zip_code: data.entity.address?.zip_code || user.address.zip_code,
              city: data.entity.address?.city || user.address.city,
              country: data.entity.address?.country || user.address.country,
            },
            projectId: data.id_ayomi,
          },
        });
      }
    } catch (err) {
      logError(err as Error);
      console.log(err);
    }
  };
}

interface NavigatorWithSaveBlob extends Navigator {
  msSaveOrOpenBlob: (blob: Blob | File, defaultName?: string) => boolean;
}

export function getDownloadInvoice() {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch({ type: 'GET_INVOICE' });
      const { user } = getState();
      const { data } = await getInvoice(user.projectId);
      if (data instanceof Blob) {
        const blob = new Blob([data], { type: 'application/pdf' });
        dispatch({ type: 'GET_INVOICE_SUCCESS' });
        // IE
        if (window.navigator && (window.navigator as NavigatorWithSaveBlob).msSaveOrOpenBlob) {
          (window.navigator as NavigatorWithSaveBlob).msSaveOrOpenBlob(data, `bill_${user.projectId}.pdf`);
          return;
        }

        // Chrome, FF
        const fileUrl = URL.createObjectURL(blob);
        if (navigator.userAgent.indexOf('Chrome') !== -1 || navigator.userAgent.indexOf('Firefox') !== -1) {
          const w = window.open(fileUrl, '_blank');
          if (w) w.focus();
        } else {
          // Safari & Opera iOS
          window.location.href = fileUrl;
        }
      } else dispatch({ type: 'GET_INVOICE_FAILED' });
    } catch (err) {
      logError(err as Error);
      dispatch({ type: 'GET_INVOICE_FAILED' });
      dispatch(addToast({
        type: 'error',
        title: 'Le téléchargement de la facture est momentanément indisponible',
        description: `Une erreur technique est survenue lors de l'envoie de votre demande. ${err}`,
      }));
    }
  };
}
